import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Trash2, Mail, CheckCircle2, Circle, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export const ContactMessagesAdminTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filter, setFilter] = useState('all'); // all | unread | resolved

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contact-messages`, { withCredentials: true });
      setMessages(data);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleRead = async (m, read) => {
    try {
      const { data } = await axios.patch(`${API}/admin/contact-messages/${m.id}`, { read }, { withCredentials: true });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
      if (selected?.id === m.id) setSelected(data);
    } catch {
      toast.error('Failed to update');
    }
  };

  const toggleResolved = async (m, resolved) => {
    try {
      const { data } = await axios.patch(`${API}/admin/contact-messages/${m.id}`, { resolved, read: true }, { withCredentials: true });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
      if (selected?.id === m.id) setSelected(data);
      toast.success(resolved ? 'Marked as resolved' : 'Reopened');
    } catch {
      toast.error('Failed to update');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/contact-messages/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Message deleted');
      if (selected?.id === deleteTarget.id) setSelected(null);
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openMessage = async (m) => {
    setSelected(m);
    if (!m.read) toggleRead(m, true);
  };

  const filtered = messages.filter((m) => {
    if (filter === 'unread') return !m.read;
    if (filter === 'resolved') return m.resolved;
    return true;
  });

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div data-testid="contact-messages-admin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontSize: '14px' }}>
          {loading
            ? 'Loading messages…'
            : `${messages.length} message${messages.length === 1 ? '' : 's'} · ${unreadCount} unread`}
        </p>
        <div className="flex gap-2 rounded-md border border-[#E5E0D8] p-1" style={{ backgroundColor: '#FFFFFF' }}>
          {[
            ['all', 'All'],
            ['unread', 'Unread'],
            ['resolved', 'Resolved']
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className="px-3 py-1 rounded-md text-xs uppercase transition-colors"
              style={{
                fontFamily: 'Manrope',
                fontWeight: 600,
                letterSpacing: '0.1em',
                backgroundColor: filter === key ? '#D95A41' : 'transparent',
                color: filter === key ? '#FFFFFF' : '#5C5C5C'
              }}
              data-testid={`contact-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && !loading ? (
        <div className="text-center py-16 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }} data-testid="contact-empty">
          <MessageSquare size={40} strokeWidth={1.2} style={{ color: '#B8B0A4' }} className="mx-auto mb-3" />
          <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
            {filter === 'unread' ? 'No unread messages.' : filter === 'resolved' ? 'Nothing resolved yet.' : 'No messages yet.'}
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-4">
          {/* List */}
          <div className="lg:col-span-2 space-y-2">
            {filtered.map((m) => {
              const isSelected = selected?.id === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => openMessage(m)}
                  className="w-full text-left rounded-md border p-4 transition-colors"
                  style={{
                    backgroundColor: isSelected ? '#FCE7DE' : '#FFFFFF',
                    borderColor: isSelected ? '#D95A41' : '#E5E0D8'
                  }}
                  data-testid={`contact-msg-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      {!m.read && (
                        <span
                          className="flex-shrink-0 rounded-full"
                          style={{ width: 8, height: 8, backgroundColor: '#D95A41' }}
                          data-testid={`contact-unread-dot-${m.id}`}
                        />
                      )}
                      <span
                        className="truncate"
                        style={{ fontFamily: 'Manrope', fontWeight: m.read ? 500 : 700, color: '#1A1A1A' }}
                      >
                        {m.name}
                      </span>
                    </div>
                    {m.resolved && (
                      <span
                        className="uppercase px-2 py-0.5 rounded flex-shrink-0"
                        style={{ backgroundColor: '#E5EFE4', color: '#2E5C41', fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em' }}
                      >
                        Resolved
                      </span>
                    )}
                  </div>
                  <div className="truncate" style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }}>
                    {m.subject || '(no subject)'}
                  </div>
                  <div className="mt-1 line-clamp-2" style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#8A8073' }}>
                    {m.message}
                  </div>
                  <div className="mt-2" style={{ fontFamily: 'Manrope', fontSize: '11px', color: '#B8B0A4' }}>
                    {formatDate(m.created_at)}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3">
            {selected ? (
              <div
                className="rounded-md border border-[#E5E0D8] p-5 md:p-7"
                style={{ backgroundColor: '#FFFFFF' }}
                data-testid="contact-detail"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <div
                      style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, fontSize: '24px', color: '#1A1A1A' }}
                      data-testid="contact-detail-subject"
                    >
                      {selected.subject || '(no subject)'}
                    </div>
                    <div style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }} className="mt-1">
                      From <strong style={{ color: '#1A1A1A' }}>{selected.name}</strong>
                      <span> · </span>
                      <a
                        href={`mailto:${selected.email}`}
                        className="hover:underline"
                        style={{ color: '#D95A41' }}
                        data-testid="contact-detail-email"
                      >
                        {selected.email}
                      </a>
                    </div>
                    <div style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#8A8073' }} className="mt-0.5">
                      {formatDate(selected.created_at)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => toggleRead(selected, !selected.read)}
                      className="rounded-md"
                      style={{ borderColor: '#E5E0D8', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                      data-testid="contact-toggle-read"
                    >
                      {selected.read ? <Circle size={13} className="mr-1" /> : <CheckCircle2 size={13} className="mr-1" />}
                      {selected.read ? 'Mark unread' : 'Mark read'}
                    </Button>
                    <Button
                      onClick={() => toggleResolved(selected, !selected.resolved)}
                      className="rounded-md"
                      size="sm"
                      style={{
                        backgroundColor: selected.resolved ? '#5C5C5C' : '#2E5C41',
                        fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px'
                      }}
                      data-testid="contact-toggle-resolved"
                    >
                      <CheckCircle2 size={13} className="mr-1" />
                      {selected.resolved ? 'Reopen' : 'Resolve'}
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setDeleteTarget(selected)}
                      className="rounded-md"
                      style={{ borderColor: '#991B1B', color: '#991B1B', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                      data-testid="contact-delete"
                    >
                      <Trash2 size={13} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>

                <div
                  className="whitespace-pre-wrap p-4 rounded-md"
                  style={{
                    fontFamily: 'Manrope',
                    color: '#3D3D3D',
                    backgroundColor: '#F9F6F0',
                    lineHeight: 1.7,
                    border: '1px solid #E5E0D8'
                  }}
                  data-testid="contact-detail-body"
                >
                  {selected.message}
                </div>

                <div className="mt-6 pt-4 border-t border-[#E5E0D8] flex justify-end">
                  <a
                    href={`mailto:${selected.email}?subject=Re:%20${encodeURIComponent(selected.subject || 'Your message to Eat From Local')}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white transition-colors"
                    style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600, fontSize: '14px' }}
                    data-testid="contact-reply-btn"
                  >
                    <Mail size={14} strokeWidth={1.5} /> Reply via email
                  </a>
                </div>
              </div>
            ) : (
              <div
                className="rounded-md border border-dashed border-[#E5E0D8] p-10 text-center"
                style={{ backgroundColor: '#F9F6F0' }}
              >
                <MessageSquare size={32} strokeWidth={1.2} style={{ color: '#B8B0A4' }} className="mx-auto mb-3" />
                <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
                  Select a message from the list to view it.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="contact-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
              Delete this message?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
              From {deleteTarget?.name} — this cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ fontFamily: 'Manrope' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              style={{ backgroundColor: '#991B1B', fontFamily: 'Manrope', fontWeight: 600 }}
              data-testid="contact-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
